import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "../utils/axios";
import { useAuth } from "./AuthContext";

const MenuAccessContext = createContext();

export const useMenuAccess = () => {
  const context = useContext(MenuAccessContext);
  if (!context) {
    throw new Error("useMenuAccess must be used within a MenuAccessProvider");
  }
  return context;
};

export const MenuAccessProvider = ({ children }) => {
  const { user } = useAuth();
  const [menuAccess, setMenuAccess] = useState({
    sales: {
      leads: false,
      appointments: false,
      tasks: false,
      analytics: false,
      orders: false,
      communications: false,
      calls: false,
    },
    orders: {
      leads: false,
      appointments: false,
      tasks: false,
      analytics: false,
      orders: false, // Will be set from database
      communications: false,
      calls: false,
    },
    telecaller: {
      leads: false,
      appointments: false,
      tasks: false,
      analytics: false,
      orders: false,
      communications: false,
      calls: false, // Will be set from database
    },
  });
  const [loading, setLoading] = useState(true);

  // Default access based on user role - starts with minimal access
  const getDefaultAccess = (role) => {
    switch (role) {
      case "admin":
        return {
          leads: true,
          appointments: true,
          orders: true,
          tasks: true,
          analytics: true,
          communications: true,
          calls: true,
        };
      case "sales":
        return {
          leads: false,
          appointments: false,
          tasks: false,
          analytics: false,
          orders: false,
          communications: false,
          calls: false,
        };
      case "orders":
        return {
          leads: false,
          appointments: false,
          tasks: false,
          analytics: false,
          orders: false,
          communications: false,
          calls: false,
        };
      case "telecaller":
        return {
          leads: false,
          appointments: false,
          tasks: false,
          analytics: false,
          orders: false,
          communications: false,
          calls: false,
        };
      default:
        return {
          leads: false,
          appointments: false,
          tasks: false,
          analytics: false,
          orders: false,
          communications: false,
          calls: false,
        };
    }
  };

  const fetchMenuAccess = async () => {
    if (!user) return;

    try {
      // Try to fetch user-specific menu access from backend
      const response = await axios.get(
        `/api/auth/users/${user.id}/menu-access`
      );

      // The backend returns access for all roles, so we use that directly
      setMenuAccess(response.data);
    } catch (error) {
      // If API doesn't exist or fails, use default access based on role
      const defaultAccess = getDefaultAccess(user.role);
      setMenuAccess({
        [user.role]: defaultAccess,
        // Initialize all roles with their defaults
        sales: getDefaultAccess("sales"),
        orders: getDefaultAccess("orders"),
        telecaller: getDefaultAccess("telecaller"),
        admin: getDefaultAccess("admin"),
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      setLoading(true);
      fetchMenuAccess();
    } else {
      setLoading(false);
    }
  }, [user?.id, user?.role]); // Only depend on user id and role, not the function

  const updateMenuAccess = async (role, menu, hasAccess) => {
    try {
      await axios.put(`/api/auth/users/menu-access`, {
        role,
        menu,
        hasAccess,
      });

      // Update local state immediately for responsive UI
      setMenuAccess((prev) => ({
        ...prev,
        [role]: {
          ...prev[role],
          [menu]: hasAccess,
        },
      }));

      // If updating current user's role, refetch to ensure sync
      if (user && user.role === role) {
        await fetchMenuAccess();
      }
    } catch (error) {
      console.error("Error updating menu access:", error);
      throw error; // Re-throw to handle in component
    }
  };

  const hasAccess = (role, menu) => {
    if (!user || !menuAccess[role]) {
      const defaultAccess = getDefaultAccess(user?.role || role)[menu] || false;
      return defaultAccess;
    }

    const result = menuAccess[role][menu] || false;
    return result;
  };

  const getCurrentUserAccess = (menu) => {
    if (!user) return false;
    return hasAccess(user.role, menu);
  };
  const value = {
    menuAccess,
    loading,
    hasAccess,
    getCurrentUserAccess,
    updateMenuAccess,
    fetchMenuAccess,
  };

  return (
    <MenuAccessContext.Provider value={value}>
      {children}
    </MenuAccessContext.Provider>
  );
};

export default MenuAccessContext;
